import { useEffect, useState } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";
import { InjuryTable } from "./InjuryTable";
import { TreatmentLog } from "./TreatmentLog";
import { GameTable } from "./GameTable";

import "bootstrap/dist/css/bootstrap.min.css";
import { Navbar } from "../Navigation/Navbar";
import { Row, Col, Menu, Typography } from "antd";
import { audioNotification as notification } from "../Common/Notifications/audioNotification";

import "../../Styles/styles-PostData.css";
import "../../Styles/styles-Settings.css";
import { RestDays } from "./RestDays";
import { DrillMergeTable } from "./DrillMergeTable";
import { NicknameTable } from "./NicknameTable";
import { OverrideDatapoint } from "./OverrideDatapoint";
import { DeleteData } from "./DeleteData";
import { TbDatabaseCog } from "react-icons/tb";
import { PlayerOrdering } from "./PlayerOrdering";
import { OverviewSettings } from "./OverviewSettings/OverviewSettings";
import { CustomColorStrategies } from "./CustomColorStrategies/CustomColorStrategies";
import { PlatformPermissions } from "./PlatformPermissions/PlatformPermissions";
import { MetricsSettings } from "./MetricsSettings";
import { WellnessSurvey } from "./WellnessSurvey";
import { PlayerGrouping } from "./PlayerGrouping";
import { PlayerOnboarding } from "./PlayerOnboarding/PlayerOnboarding";
import { useAuth0 } from "@auth0/auth0-react";
import { SettingTitle } from "./SettingTitle";
import { TeamOutlined, UserOutlined, HighlightOutlined, LockOutlined } from "@ant-design/icons";
import { GiWhistle } from "react-icons/gi";
import { IoMdAirplane } from "react-icons/io";
import {FaChalkboardTeacher, FaRegAddressCard} from "react-icons/fa";
import { ImList2 } from "react-icons/im";
import {
  MdDriveFileRenameOutline,
  MdOutlineSettings,
  MdOutlinePersonalInjury,
  MdOutlineLibraryBooks,
  MdDeleteOutline
  } from "react-icons/md";
import { PiListNumbers } from "react-icons/pi";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { RiSurveyLine } from "react-icons/ri";
import { GrTrigger } from "react-icons/gr";
import { CgColorPicker } from "react-icons/cg";
import { TeamBaseline } from "./TeamBaseline";
import { PlayerBaseline } from "./PlayerBaseline";
import { AutomaticActions } from "./AutomaticActions/AutomaticActions";
import { ReportBulletPicker } from "./ReportBulletPicker/ReportBulletPicker";
import {CoachOnboarding} from "./CoachOnboarding";
import { HelpButton } from "../Common/HelpButton";
import { TfiLock } from "react-icons/tfi";
import { ActivityLog } from "./ActivityLog";

const ROSTER_TEAMS = [
  "Demo Team",
  "Football Demo Team",
  "Montana WSOC",
  "Frenship HS Football",
  "Orlando Pride",
  "North Carolina Courage",
  "Racing Louisville WSOC",
  "Utah Warriors Rugby",
  "N2AthleteX",
  "Summerville Football",
  "Summerville Athletics",
  "Pittsburgh Steelers",
  "Temple University Football",
  "New York Rangers",
  "Las Vegas Raiders",
];

const ROSTER_ACCESS_ACCOUNTS = [
  "coachrubyn@berkeley.edu",
  "bnichols@mitty.com",
  "chapp103@d.umn.edu",
  "trevor.goldstein@msgsports.com",
  "antony.organ@msgsports.com",
  "coachrobinson@purdue.edu",
  "tulsastrength2025@gmail.com",
  "elyas.himmelgreen@tampabaysunfc.com",
  "sami.diamond@superleaguetampabay.com",
  "andrea.hayden@lynxbasketball.com",
  "judkinst@steelers.nfl.com",
  "gsmith@bayfc.com",
  "jdiaz@athletics.gatech.edu"
];

export const displayValidationErrorMessages = (errors) => {
  if(errors?.length) {
    errors.forEach( (e) => {
      const severity = e?.severity
      switch (severity) {
        case "error":
          notification.error({description: e?.message})
          break;
        case "warning":
          notification.warning({description: e?.message})
          break;
        default:
          notification.info({description: e?.message})
      }
    })
  }
}

export const TeamSettings = (props) => {
  const trainingAPI = new TrainingAPI();

  const {
    info: { 
      display_name: teamName,
      team_logo,
      bullet_picker_enabled,
      custom_colors_enabled,
      platform_permissions_enabled
    },
    players,
    allPlayers,
    playerGroups,
    misc: {
      user_permissions: userPermissions
    }
  } = useSelector(({ team }) => team);

  const { user } = useAuth0();
  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  const [possiblePositions, setPossiblePositions] = useState([]);
  const [sport, setSport] = useState(undefined);
  const [baselineOverviewData, setBaselineOverviewData] = useState([]);
  const [allMetrics, setAllMetrics] = useState([]);

  const [selectedMenuKeys, setSelectedMenuKeys] = useState([]);

  const getBaselineOverviewData = () => {
    trainingAPI.getBaselineOverview().then((data) => {
      setBaselineOverviewData(data);
    });
  };

  const getPossiblePositions = () => {
    trainingAPI.getPositions(true).then((data) => {
      setPossiblePositions(data.positions);
      setSport(data.sport);
    });
  };

  const getAllMetrics = () => {
    trainingAPI.getTeamMetrics(false, false, false, false, null).then((data) => {
      setAllMetrics(data);
    });
  };

  useEffect(() => {
    getBaselineOverviewData();
    getPossiblePositions();
    getAllMetrics();
    
    setSelectedMenuKeys(["overview"]);
  }, []);

  let contents = {
    "overview": (
      <OverviewSettings />
    ),
    "metrics": (
      <MetricsSettings
        allMetrics={allMetrics}
        getAllMetrics={getAllMetrics}
      />
    ),
    "activity-log": (
      <ActivityLog />
    ),
    "baselines-team": (
      <TeamBaseline
        allMetrics={allMetrics}
        sport={sport}
        getBaselineOverviewData={getBaselineOverviewData}
      />
    ),
    "baselines-player": (
      <PlayerBaseline
        baselineOverviewData={baselineOverviewData}
        getBaselineOverviewData={getBaselineOverviewData}
        possiblePositions={possiblePositions}
        sport={sport}
      />
    ),
    "calendar-games": <GameTable />,
    "calendar-rest": <RestDays />,
    "players-roster": <PlayerOnboarding userPermissions={userPermissions}/>,
    "players-nicknames": <NicknameTable players={allPlayers} />,
    "players-ordering": (
      <PlayerOrdering
        players={players}
        player_groups={playerGroups}
        team_name={teamName}
      />
    ),
    "players-groups": <PlayerGrouping />,
    "health-wellness": <WellnessSurvey />,
    "health-injuries": <InjuryTable players={players} team_name={teamName} />,
    "treatment-log": <TreatmentLog />,
    "drills-management": <DrillMergeTable />,
    "override-datapoint": <OverrideDatapoint allMetrics={allMetrics} />,
    "delete-data": <DeleteData />,
    "automatic-actions": <AutomaticActions />,
    "reports": <ReportBulletPicker />,
    "custom-coloring": <CustomColorStrategies />,
    "platform-permissions": <PlatformPermissions userPermissions={userPermissions}/>,
    "coach-onboarding": <CoachOnboarding />
  };

  let titles = {
    "overview": "Overview",
    "metrics": "Metrics",
    "activity-log": "Activity Log",
    "baselines-team": "Team Baseline",
    "baselines-player": "Player Baseline",
    "calendar-games": "Game Days",
    "calendar-rest": "Calendar Tags",
    "players-roster": "Player Onboarding",
    "players-nicknames": "Player Nicknames",
    "players-ordering": "Player Ordering",
    "players-groups": "Player Groups",
    "health-wellness": "Wellness Survey",
    "health-injuries": "Injury Table",
    "treatment-log": "Treatment Log",
    "drills-management": "Manage Drills",
    "override-datapoint": "Override Datapoint",
    "delete-data": "Delete Data",
    "automatic-actions": "Automatic Actions",
    "coach-onboarding": "Coach Onboarding",
    "custom-coloring": "Custom Coloring",
    "platform-permissions": "Platform Permissions",
  };

  const DenseMenuItem = ({ children, ...props }) => (
    <Menu.Item
      {...props}
      style={{ height: 30, lineHeight: "30px", margin: "2px 0px" }}
    >
      {children}
    </Menu.Item>
  );

  const CustomizedMenuItemGroup = ({ title }) => (
    <Menu.ItemGroup
      key="baselines"
      title={<div style={{ fontSize: 12 }}>{title}</div>}
      style={{
        height: 30,
        lineHeight: 30,
      }}
    />
  );

  return (
    <>
      <Navbar />
      <HelpButton page="Team Settings" />
      <Row>
        <Col span={20} offset={2}>
          <div
            style={{
              display: "flex",
              gap: 16,
              margin: "24px 16px",
              alignItems: "center",
            }}
          >
            <img
              src={team_logo}
              style={{ height: 48, width: 48, objectFit: "contain" }}
            />
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {teamName} <span style={{ color: "gray" }}>({user.email})</span>
            </Typography.Title>
          </div>
        </Col>
      </Row>
      <div>
        <Row justify={"center"} style={{ paddingBottom: 24 }}>
          <Col span={4}>
            <Menu
              selectedKeys={selectedMenuKeys}
              onSelect={({ key }) => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setSelectedMenuKeys([key])
              }}
            >
              <CustomizedMenuItemGroup title="General" />
              <DenseMenuItem key="overview" icon={<MdOutlineSettings />}>
                Overview
              </DenseMenuItem>
              <DenseMenuItem key="metrics" icon={<PiListNumbers style={{ marginBottom: 3 }} />}>
                Metrics
              </DenseMenuItem>
              <DenseMenuItem key="activity-log" icon={<ImList2 style={{ marginBottom: 3 }} />}>
                Activity Log
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Baselines" />
              <DenseMenuItem key="baselines-team" 
              icon={userPermissions?.ViewTeamBaselines ? <TeamOutlined /> : <TfiLock/>}>
                Team Baseline
              </DenseMenuItem>
              <DenseMenuItem key="baselines-player" 
              icon={userPermissions?.ViewPlayerBaselines ? <UserOutlined /> : <TfiLock/>}>
                Player Baseline
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Calendar" />
              <DenseMenuItem
                key="calendar-games"
                icon={<GiWhistle style={{ marginBottom: 3 }} />}
              >
                Games
              </DenseMenuItem>
              <DenseMenuItem
                key="calendar-rest"
                icon={<IoMdAirplane style={{ marginBottom: 3 }} />}
              >
                Calendar Tags
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Players" />
              <DenseMenuItem
                key="players-roster"
                icon={userPermissions?.RosterViewPlayers ? <FaRegAddressCard style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Roster
              </DenseMenuItem>
              <DenseMenuItem
                key="players-nicknames"
                icon={<MdDriveFileRenameOutline style={{ marginBottom: 3 }} />}
              >
                Nicknames
              </DenseMenuItem>
              <DenseMenuItem
                key="players-ordering"
                icon={<PiListNumbers style={{ marginBottom: 3 }} />}
              >
                Ordering
              </DenseMenuItem>
              <DenseMenuItem
                key="players-groups"
                icon={<HiOutlineRectangleGroup style={{ marginBottom: 3 }} />}
              >
                Groups
              </DenseMenuItem>
              {
                isStaffEmail && (
                  <DenseMenuItem
                    key="coach-onboarding"
                    icon={<FaChalkboardTeacher style={{ marginBottom: 3 }} />}
                  >
                    Coach Onboarding
                  </DenseMenuItem>
                )
              }
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Health" />
              <DenseMenuItem
                key="health-wellness"
                icon={<RiSurveyLine style={{ marginBottom: 3 }} />}
              >
                Wellness
              </DenseMenuItem>
              <DenseMenuItem
                key="health-injuries"
                icon={<MdOutlinePersonalInjury style={{ marginBottom: 3 }} />}
              >
                Injuries
              </DenseMenuItem>
              <DenseMenuItem
                  key="treatment-log"
                  icon={<ImList2 style={{ marginBottom: 3 }} />}
                >
                  Treatment Log
                </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Drills" />
              <DenseMenuItem
                key="drills-management"
                icon={<MdOutlineLibraryBooks style={{ marginBottom: 3 }} />}
              >
                Management
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Manual Changes" />
              <DenseMenuItem
                key="override-datapoint"
                icon={userPermissions?.OverrideDataPoints ? <TbDatabaseCog style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Override Datapoint
              </DenseMenuItem>
              <DenseMenuItem
                key="delete-data"
                icon={userPermissions?.DeleteDatapoints ? <MdDeleteOutline style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Delete Data
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Automatic Changes" />
              <DenseMenuItem
                key="automatic-actions"
                icon={<GrTrigger style={{ marginBottom: 3 }} />}
              >
                Automatic Actions
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Elite Features" />
                <DenseMenuItem
                  key="reports"
                  icon={bullet_picker_enabled ? <ImList2 style={{ marginBottom: 3 }} /> :<TfiLock/> }
                >
                  Pick Your Bullets
                </DenseMenuItem>
                {<DenseMenuItem
                  key="custom-coloring"
                  icon={custom_colors_enabled ? <HighlightOutlined style={{ marginBottom: 3 }} /> :<TfiLock/> }
                >
                  Custom Coloring
                </DenseMenuItem>
                }
                <DenseMenuItem
                  key="platform-permissions"
                  icon={platform_permissions_enabled ? <LockOutlined style={{ marginBottom: 3 }} /> :<TfiLock/>}
                >
                  Platform Permissions
                </DenseMenuItem>
            </Menu>
          </Col>
          <Col span={16} style={{ textAlign: "left", paddingLeft: 48 }}>
            <SettingTitle title={titles[selectedMenuKeys[0] || ""]} />
            {contents[selectedMenuKeys[0] || ""]}
          </Col>
        </Row>
      </div>
      {/* <Footer style={{color: 'grey', marginTop:'70px'}}>GPS DataViz 2021</Footer> */}
    </>
  );
};
